import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { Download } from "lucide-react";
import { isCSV, isJSON, flattenObject } from "@shadcn-in-nx/utils";

function processCSV(csvData) {
    // Check if input is a valid CSV string
    if (!csvData || typeof csvData !== "string") {
        throw new Error("Invalid input data");
    }

    try {
        // Handle both cases where outer quotes might exist
        let data = csvData;
        if (data.startsWith('"') && data.endsWith('"')) {
            // Remove the outer quotes if they exist
            data = data.substring(1, data.length - 1);
        }

        // Split into lines, handling the newline character carefully
        const lines = data.split("\\n");

        // Process each line
        const processedLines = lines.map((line) => {
            // We'll manually parse each line to handle the complex escaping correctly
            const result = [];
            let currentField = "";
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                const nextChar = line[i + 1];

                // Handle escaped quotes inside already quoted fields
                if (char === "\\" && nextChar === '"') {
                    currentField += '"';
                    i++; // Skip the next character (the quote)
                    continue;
                }

                // Toggle quote state
                if (char === '"') {
                    inQuotes = !inQuotes;
                    continue;
                }

                // Handle comma outside quotes - field delimiter
                if (char === "," && !inQuotes) {
                    result.push(currentField);
                    currentField = "";
                    continue;
                }

                // Normal character - add to current field
                currentField += char;
            }

            // Add the last field
            if (currentField) {
                result.push(currentField);
            }

            return result;
        });

        return processedLines;
    } catch (error) {
        console.error("Error processing CSV:", error);
        throw new Error("Failed to process CSV data");
    }
}

const JsonParentTable = ({ data }) => {
    const dataParsed = React.useMemo(() => {
        if (isCSV(data)) {
            return processCSV(data);
        } else if (isJSON(data)) {
            return JSON.parse(data);
        }
        return data;
    }, [data]);

    const downloadAsJSON = React.useCallback(() => {
        const jsonString = JSON.stringify(dataParsed, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "data.json");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [dataParsed]);

    const downloadAsCSV = React.useCallback(() => {
        let csvContent = "";

        if (Array.isArray(dataParsed)) {
            // Flatten each object in the array
            const flattenedData = dataParsed.map((item) => flattenObject(item));

            // Get all unique keys from flattened objects
            const keys = [
                ...new Set(flattenedData.flatMap((item) => Object.keys(item))),
            ];

            // Add headers
            csvContent = keys.join(",") + "\n";

            // Add data rows
            csvContent += flattenedData
                .map((item) => {
                    return keys
                        .map((key) => {
                            const value =
                                item[key] !== undefined ? item[key] : "";
                            // Escape quotes and wrap in quotes if contains comma or newline
                            const formattedValue =
                                typeof value === "string" &&
                                (value.includes(",") ||
                                    value.includes("\n") ||
                                    value.includes('"'))
                                    ? `"${value.replace(/"/g, '""')}"`
                                    : value;
                            return formattedValue;
                        })
                        .join(",");
                })
                .join("\n");
        } else if (typeof dataParsed === "object" && dataParsed !== null) {
            // For single objects, flatten and convert to two-column format
            const flattenedObject = flattenObject(dataParsed);

            csvContent = "Key,Value\n";
            csvContent += Object.entries(flattenedObject)
                .map(([key, value]) => {
                    const formattedValue =
                        typeof value === "string" &&
                        (value.includes(",") ||
                            value.includes("\n") ||
                            value.includes('"'))
                            ? `"${value.replace(/"/g, '""')}"`
                            : value;
                    return `${key},${formattedValue}`;
                })
                .join("\n");
        }

        // Create and trigger download
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [dataParsed]);
    return (
        <div className="w-full">
            <div className="mb-2 flex items-center justify-end">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                            <Download />
                            Download
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={downloadAsCSV}>
                            Download CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={downloadAsJSON}>
                            Download JSON
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <JsonTable data={dataParsed} />
        </div>
    );
};

const JsonTable = ({ data }) => {
    const renderValue = (value) => {
        if (value === null || value === undefined) return "N/A";
        if (typeof value === "boolean") return value.toString();
        if (typeof value === "number") return value;

        // Handle nested objects
        if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            value !== null
        ) {
            return (
                <div className="nested-table overflow-x-auto">
                    <JsonTable data={value} />
                </div>
            );
        }

        return value.toString();
    };

    // If the data is an array, render it differently
    if (Array.isArray(data)) {
        // Handle array of primitive values
        if (
            data.length === 0 ||
            typeof data[0] !== "object" ||
            data[0] === null
        ) {
            const displayData = data.slice(0, 300);
            return (
                <div className="rounded-md border min-w-full">
                    <Table className="min-w-full">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="min-w-[200px]">
                                    Value
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {displayData.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell className="min-w-[200px] break-all">
                                        {renderValue(item)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {data.length > 300 && (
                        <div className="p-2 text-sm text-gray-500 text-center">
                            Showing first 300 items out of {data.length} total
                            items
                        </div>
                    )}
                </div>
            );
        }

        // Get all possible keys from all objects in the array
        const keys = [
            ...new Set(
                data.flatMap((item) =>
                    typeof item === "object" && item !== null
                        ? Object.keys(item)
                        : []
                )
            ),
        ];

        const displayData = data.slice(0, 300);
        return (
            <div className="rounded-md border min-w-full">
                <Table className="min-w-full">
                    <TableHeader>
                        <TableRow>
                            {keys.map((key) => (
                                <TableHead
                                    key={key}
                                    className="min-w-[150px] whitespace-nowrap"
                                >
                                    {key}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {displayData.map((item, index) => (
                            <TableRow key={index}>
                                {keys.map((key) => (
                                    <TableCell
                                        key={`${index}-${key}`}
                                        className="min-w-[150px] break-all"
                                    >
                                        {item && item[key]
                                            ? renderValue(item[key])
                                            : "N/A"}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {data.length > 300 && (
                    <div className="p-2 text-sm text-gray-500 text-center">
                        Showing first 300 items out of {data.length} total items
                    </div>
                )}
            </div>
        );
    }

    // For regular objects, render as before
    return (
        <div className="rounded-md border min-w-full">
            <Table className="min-w-full">
                <TableHeader>
                    <TableRow>
                        <TableHead className="min-w-[150px] w-1/3">
                            Key
                        </TableHead>
                        <TableHead className="min-w-[200px]">Value</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {typeof data === "number" || typeof data === "string" ? (
                        <TableRow>
                            <TableCell className="min-w-[150px]">
                                Value
                            </TableCell>
                            <TableCell className="min-w-[200px] break-all">
                                {data}
                            </TableCell>
                        </TableRow>
                    ) : (
                        Object.entries(data).map(([key, value]) => (
                            <TableRow key={key}>
                                <TableCell className="font-medium min-w-[150px] break-all">
                                    {key}
                                </TableCell>
                                <TableCell className="min-w-[200px]">
                                    {Array.isArray(value) ? (
                                        <div className="w-full overflow-x-auto">
                                            <JsonTable data={value} />
                                        </div>
                                    ) : (
                                        <div className="break-all">
                                            {renderValue(value)}
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default JsonParentTable;
